import React, { BaseSyntheticEvent, ChangeEvent, ReactNode, useCallback } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { newClientLabelActions } from '../model/slices/newClientLabelSlice'
import { selectIsLoading } from '../model/selectors/selectIsLoading'
import { selectClientLabelName } from '../model/selectors/selectClientLabelName'
import { selectClientLabelColor } from '../model/selectors/selectClientLabelColor'
import { useAppDispatch } from '../../../shared/lib/hooks/useAppDispatch/useAppDispatch'
import { useAppSelector } from '../../../shared/lib/hooks/useAppSelector/useAppSelector'
import { modalActions } from '../../../entities/Modal'
import { ModalForm } from '../../../shared/ui/ModalForm/ModalForm'
import { Loader } from '../../../shared/ui/Loader/Loader'
import { LabelColorPicker } from '../../../entities/ClientLabel'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import { addNewClientLabel } from '../model/services/addNewClientLabel'
import { selectNewClientLabel } from '../model/selectors/selectNewClientLabel'
import { selectClientLabels } from '../../DisplayClientLabels'

const style = {
	width: 400,
	p: '2rem 2rem',
	gap: 2
}

const inputStyles = {
	width: '100%'
}

export const AddNewClientLabelModal = () => {
	const dispatch = useAppDispatch()
	const {
		formState: { errors, isValid },
		register,
		handleSubmit,
		reset
	} = useForm({ mode: 'onBlur' })

	const isLoading = useAppSelector(selectIsLoading)
	const label = useAppSelector(selectNewClientLabel)
	const labelName = useAppSelector(selectClientLabelName)
	const labelColor = useAppSelector(selectClientLabelColor)
	const clientLabels = useAppSelector(selectClientLabels)

	const handleLabelNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		e.preventDefault()
		dispatch(newClientLabelActions.setLabelName(e.target.value))
	}, [dispatch])

	const handleLabelColorChange = useCallback((value: string) => {
		dispatch(newClientLabelActions.setLabelColor(value))
	}, [dispatch])

	const handleCancel = () => {
		dispatch(modalActions.hideModal())
		dispatch(newClientLabelActions.resetNewClientLabel())
		reset?.()
	}

	const onSubmit: SubmitHandler<{}> = async (_, event) => {
		event?.preventDefault()
		const result = await dispatch(addNewClientLabel(label))
		if (result.meta.requestStatus === 'fulfilled') {
			dispatch(newClientLabelActions.resetNewClientLabel())
			dispatch(modalActions.hideModal())
			reset()
		}
	}

	if (isLoading) return <Loader />

	return (
		<ModalForm
			handleCancel={handleCancel}
			disabled={!isValid}
			handleOnSubmit={(e: BaseSyntheticEvent) =>
				handleSubmit(onSubmit({}, e))
			}
			modalTitle={'Add new client label'}
			styles={style}
			disableCloseOnBackdropClick
		>
			<Stack
				direction="column"
				spacing={3}
			>
				<TextField
					required
					type="text"
					sx={inputStyles}
					variant="outlined"
					label="Label name"
					error={!!errors?.labelName?.message}
					helperText={(errors?.labelName?.message as ReactNode) || ''}
					{...register('labelName', {
						required: 'Field is required',
						validate: (name) => !clientLabels.some(
								(label) => label.labelName === name) ||
							'Label with this name already exists'
					})}
					onChange={handleLabelNameChange}
					value={labelName}
				/>
				<LabelColorPicker
					register={register}
					errors={errors}
					labelName={labelName}
					labelColor={labelColor}
					onColorChange={handleLabelColorChange}
					labelId={'addLabelColor'}
					styles={inputStyles}
				/>
			</Stack>
		</ModalForm>
	)
}
